import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';

import DnaBackground from '../components/DnaBackground';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import TypeAvatar from '../components/TypeAvatar';
import Seo from '../components/Seo';
import { getFounderType, founderTypes, getTypeProfilePath } from '../data/founderTypes';
import { applyTypeTheme, clearTypeTheme } from '../utils/founderIdentity';
import { formatFounderTitle } from '../utils/textUtils';

const Page = styled.div`
  min-height: 100vh;
  padding: 1.5rem 1.25rem 3rem;
  position: relative;
  z-index: 1;
`;

const Container = styled.div`
  max-width: 720px;
  margin: 0 auto;
`;

const Hero = styled.div`
  text-align: center;
  margin: 1.5rem 0 2rem;
`;

const Title = styled.h1`
  font-family: var(--font-display);
  font-size: clamp(2rem, 5vw, 2.5rem);
  font-weight: 800;
  color: var(--text-primary);
  margin: 1rem 0 0.5rem;
  letter-spacing: -0.03em;
`;

const Tagline = styled.p`
  font-size: 1.15rem;
  color: var(--type-accent, var(--primary-color));
  font-weight: 600;
  margin-bottom: 1rem;
`;

const Description = styled.p`
  color: var(--text-secondary);
  line-height: 1.7;
  font-size: 1.05rem;
`;

const Card = styled.section`
  background: rgba(18, 18, 28, 0.75);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.25rem;
  backdrop-filter: blur(10px);
`;

const SectionTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.75rem;
`;

const List = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  color: var(--text-secondary);
  line-height: 1.65;
`;

const CtaButton = styled(motion(Link))`
  display: block;
  text-align: center;
  margin-top: 2rem;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  font-weight: 700;
  color: white;
  background: var(--type-accent, var(--primary-color));
  color: #0C0F14;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
`;

const CompatibleGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const CompatibleLink = styled(Link)`
  padding: 0.5rem 0.85rem;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-primary);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);

  &:hover {
    border-color: var(--type-accent, var(--primary-color));
  }
`;

const TypeProfile = () => {
  const { typeId } = useParams();
  const navigate = useNavigate();
  const type = getFounderType(typeId);

  useEffect(() => {
    if (!type) {
      navigate('/types', { replace: true });
      return;
    }
    const canonical = type.slug || type.id;
    if (typeId !== canonical) {
      navigate(getTypeProfilePath(type), { replace: true });
      return;
    }
    applyTypeTheme(type.id);
    return () => clearTypeTheme();
  }, [type, typeId, navigate]);

  if (!type) return null;

  return (
    <Page>
      <Seo
        title={formatFounderTitle(type.title)}
        description={`${type.tagline} ${type.shortDescription}`}
        path={getTypeProfilePath(type)}
      />
      <DnaBackground />
      <Container>
        <SiteHeader showCta />
        <Hero>
          <TypeAvatar type={type} size={120} showCode />
          <Title>{formatFounderTitle(type.title)}</Title>
          <Tagline>{type.tagline}</Tagline>
          <Description>{type.description}</Description>
        </Hero>

        <Card>
          <SectionTitle>Strengths</SectionTitle>
          <List>{type.strengths.map((s) => <li key={s}>{s}</li>)}</List>
        </Card>

        <Card>
          <SectionTitle>Watch out for</SectionTitle>
          <List>{type.challenges.map((c) => <li key={c}>{c}</li>)}</List>
        </Card>

        {type.famousFounders?.length > 0 && (
          <Card>
            <SectionTitle>Often seen in founders like</SectionTitle>
            <List>{type.famousFounders.map((f) => <li key={f}>{f}</li>)}</List>
          </Card>
        )}

        {type.compatibleWith?.length > 0 && (
          <Card>
            <SectionTitle>Works well with</SectionTitle>
            <CompatibleGrid>
              {type.compatibleWith.map((id) => {
                const compat = founderTypes[id];
                return compat ? (
                  <CompatibleLink key={id} to={getTypeProfilePath(compat)}>
                    {formatFounderTitle(compat.title)}
                  </CompatibleLink>
                ) : null;
              })}
            </CompatibleGrid>
          </Card>
        )}

        <CtaButton to="/" whileTap={{ scale: 0.98 }}>
          Take the free test: am I {type.title.replace('The ', 'a ')}?
        </CtaButton>
        <SiteFooter />
      </Container>
    </Page>
  );
};

export default TypeProfile;
